/**
 * Estructura común de las fichas de detalle en variante «tarjetas».
 *
 * El problema que resuelve: si la cabecera y las pestañas viven dentro del
 * contenedor que hace scroll, hay que tapar el contenido con `z-index` y
 * fondos opacos, y siempre acaba asomando algo. Aquí la cabecera queda FUERA
 * del área desplazable, así que no hay nada que tapar.
 *
 * Con `isCards === false` no pinta caja alguna: los hijos vuelven a ser items
 * directos del formulario (`display: contents`), de modo que la ficha clásica
 * conserva su `gap` y su scroll originales.
 *
 * Uso:
 *   <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={...}>
 *     <FormCardsShell isCards={isCards} header={<h1>…</h1>} tabs={<Tabs />}>
 *       {pane}
 *     </FormCardsShell>
 *   </form>
 *
 * Y cada sección del contenido debe ir como `<div className="dv-tab-pane sf2-pane">`
 * para que `.sf2-pane > .dv-section` la pinte como tarjeta.
 */
import React from 'react';

/**
 * Tarjeta simple para la variante «tarjetas». En la clásica no envuelve nada:
 * devuelve los hijos tal cual, así la ficha de siempre no cambia.
 */
export function Sf2Card({ isCards = false, title = '', editing = false, children }) {
  if (!isCards) return <>{children}</>;
  return (
    <div className="sf2-card">
      <h3>
        {title}
        {editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}
      </h3>
      {children}
    </div>
  );
}

/**
 * Envuelve las pestañas en un `fieldset` desactivado cuando no se está editando,
 * pero SOLO en la variante tarjetas. Las fichas clásicas que nunca han tenido
 * `fieldset` (router, chasis…) siguen igual: ni un nodo de más en el DOM.
 */
export function Sf2PaneGuard({ isCards = false, editing = false, children }) {
  if (!isCards) return <>{children}</>;
  return <fieldset className="dv-fieldset" disabled={!editing}>{children}</fieldset>;
}

export default function FormCardsShell({ isCards = false, header = null, tabs = null, children }) {
  if (!isCards) {
    return (
      <>
        {header}
        {tabs}
        {children}
      </>
    );
  }

  return (
    <>
      <div className="sf2-headarea">
        {header}
        {tabs}
      </div>
      <div className="sf2-scroll">{children}</div>
    </>
  );
}
